import React from 'react';
import PropTypes from 'prop-types';

/**
 * TeeSync Professional Brand Mark
 *
 * A minimalist geometric logo featuring a stylised golf tee and ball.
 * Uses CSS variables for theming support.
 *
 * Sizes:
 * - xs: 24px (inline text)
 * - sm: 32px (compact)
 * - md: 40px (mobile header)
 * - lg: 48px (desktop header)
 * - xl: 64px (hero/splash)
 * - 2xl: 96px (landing page)
 */

const sizes = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 64,
  '2xl': 96,
};

function Logo({
  size = 'md',
  className = '',
  variant = 'default',
  showText = false,
  textClassName = '',
}) {
  const dimension = typeof size === 'number' ? size : sizes[size] || sizes.md;

  // Colour variants
  const colours = {
    default: {
      primary: 'currentColor',
      secondary: 'currentColor',
    },
    branded: {
      primary: 'var(--color-primary-600)',
      secondary: 'var(--color-primary-500)',
    },
    white: {
      primary: '#ffffff',
      secondary: '#ffffff',
    },
    dark: {
      primary: '#1f2937',
      secondary: '#374151',
    },
  };

  const { primary, secondary } = colours[variant] || colours.default;

  return (
    <span className={`inline-flex items-center ${showText ? 'gap-2' : ''} ${className}`}>
      <svg
        width={dimension}
        height={dimension}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="TeeSync logo"
      >
        <title>TeeSync</title>

        {/* Golf ball - positioned top right */}
        <circle
          cx="30"
          cy="14"
          r="8"
          fill={primary}
          opacity="0.9"
        />

        {/* Golf ball dimples - subtle detail */}
        <circle cx="28" cy="12" r="1.2" fill={secondary} opacity="0.3" />
        <circle cx="32" cy="13" r="1" fill={secondary} opacity="0.3" />
        <circle cx="30" cy="16" r="1.1" fill={secondary} opacity="0.3" />

        {/* Golf tee - angled elegantly */}
        <path
          d="M20 18 L24 38 L28 38 L24 18 Z"
          fill={primary}
          opacity="0.85"
        />

        {/* Tee base - wider foundation */}
        <path
          d="M22 38 L30 38 L31 42 L21 42 Z"
          fill={primary}
        />

        {/* Swing arc - dynamic motion element */}
        <path
          d="M8 28 Q 18 16, 28 22"
          stroke={secondary}
          strokeWidth="2.5"
          strokeLinecap="round"
          fill="none"
          opacity="0.6"
        />
      </svg>

      {showText && (
        <span className={`font-semibold tracking-tight ${textClassName}`}>
          TeeSync
        </span>
      )}
    </span>
  );
}

Logo.propTypes = {
  size: PropTypes.oneOfType([
    PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl']),
    PropTypes.number,
  ]),
  className: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'branded', 'white', 'dark']),
  showText: PropTypes.bool,
  textClassName: PropTypes.string,
};

export default Logo;
